import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import type { CanvasViewGridFilterControl } from "../types";

export interface ViewGridFilterFieldOption {
  key: string;
  label: string;
}

interface Props {
  controls: CanvasViewGridFilterControl[];
  fields: ViewGridFilterFieldOption[];
  onChange: (controls: CanvasViewGridFilterControl[]) => void;
}

export function ViewGridFilterControlsEditor({
  controls,
  fields,
  onChange,
}: Props) {
  const update = (
    index: number,
    patch: Partial<CanvasViewGridFilterControl>,
  ) => {
    onChange(
      controls.map((control, position) =>
        position === index ? { ...control, ...patch } : control,
      ),
    );
  };
  const available = fields.find(
    (field) => !controls.some((control) => control.field === field.key),
  );

  return (
    <Stack spacing={1}>
      <Typography level="body-sm" fontWeight="md">
        Field filters
      </Typography>
      <FormHelperText>
        Dropdown choices filter all pages. All removes that field condition.
      </FormHelperText>
      {controls.map((control, index) => {
        const options = control.options;
        return (
          <Box
            key={`${control.field}-${index}`}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              p: 1,
              borderRadius: "sm",
            }}
          >
            <Stack spacing={1}>
              <FormControl size="sm">
                <FormLabel>Filter field</FormLabel>
                <Select
                  aria-label="Filter field"
                  value={control.field}
                  onChange={(_, value) => {
                    const next = fields.find(
                      (candidate) => candidate.key === value,
                    );
                    if (next)
                      update(index, {
                        field: next.key,
                        label: next.label,
                        default_value: null,
                      });
                  }}
                >
                  {fields.map((candidate) => (
                    <Option
                      key={candidate.key}
                      value={candidate.key}
                      disabled={
                        candidate.key !== control.field &&
                        controls.some((other) => other.field === candidate.key)
                      }
                    >
                      {candidate.label}
                    </Option>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="sm">
                <FormLabel>Filter label</FormLabel>
                <Input
                  aria-label="Filter label"
                  value={control.label}
                  onChange={(event) =>
                    update(index, { label: event.target.value })
                  }
                />
              </FormControl>
              <Stack direction="row" spacing={1}>
                <FormControl size="sm" sx={{ flex: 1 }}>
                  <FormLabel>Operator</FormLabel>
                  <Select
                    aria-label="Operator"
                    value={control.operator}
                    onChange={(_, value) =>
                      value && update(index, { operator: value })
                    }
                  >
                    <Option value="eq">Is</Option>
                    <Option value="ne">Is not</Option>
                  </Select>
                </FormControl>
                <FormControl size="sm" sx={{ flex: 1 }}>
                  <FormLabel>Option source</FormLabel>
                  <Input value="Configured choices" disabled />
                </FormControl>
              </Stack>
              <Stack spacing={1}>
                {control.options.map((option, optionIndex) => (
                  <Stack key={optionIndex} direction="row" spacing={1}>
                    <Input
                      aria-label={`Option ${optionIndex + 1} label`}
                      placeholder="Label"
                      value={option.label}
                      onChange={(event) =>
                        update(index, {
                          options: control.options.map((item, position) =>
                            position === optionIndex
                              ? { ...item, label: event.target.value }
                              : item,
                          ),
                        })
                      }
                    />
                    <Input
                      aria-label={`Option ${optionIndex + 1} value`}
                      placeholder="Value"
                      value={option.value}
                      onChange={(event) =>
                        update(index, {
                          options: control.options.map((item, position) =>
                            position === optionIndex
                              ? { ...item, value: event.target.value }
                              : item,
                          ),
                        })
                      }
                    />
                    <Button
                      variant="plain"
                      color="danger"
                      size="sm"
                      aria-label={`Remove option ${optionIndex + 1}`}
                      disabled={control.options.length <= 1}
                      onClick={() =>
                        update(index, {
                          options: control.options.filter(
                            (_, position) => position !== optionIndex,
                          ),
                          default_value: null,
                        })
                      }
                    >
                      Remove
                    </Button>
                  </Stack>
                ))}
                <Button
                  variant="outlined"
                  size="sm"
                  onClick={() =>
                    update(index, {
                      options: [
                        ...control.options,
                        {
                          label: "New option",
                          value: `option-${control.options.length + 1}`,
                        },
                      ],
                    })
                  }
                >
                  Add option
                </Button>
              </Stack>
              <FormControl size="sm">
                <FormLabel>Default value</FormLabel>
                <Select
                  aria-label="Default value"
                  value={control.default_value ?? ""}
                  onChange={(_, value) =>
                    update(index, { default_value: value || null })
                  }
                >
                  <Option value="">All</Option>
                  {options.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </FormControl>
              <Button
                size="sm"
                color="danger"
                variant="plain"
                onClick={() =>
                  onChange(controls.filter((_, position) => position !== index))
                }
              >
                Remove field filter
              </Button>
            </Stack>
          </Box>
        );
      })}
      <Button
        size="sm"
        variant="outlined"
        disabled={!available || controls.length >= 12}
        onClick={() =>
          available &&
          onChange([
            ...controls,
            {
              field: available.key,
              label: available.label,
              operator: "eq",
              option_source: "static",
              options: [{ label: "Option", value: "option" }],
              default_value: null,
            },
          ])
        }
      >
        Add field filter
      </Button>
    </Stack>
  );
}
